// src/components/services/ServiceDetail.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  fetchService,
  deleteService,
  toggleServiceStatus,
  fetchServiceAgencies,
  clearServiceError,
} from '../../store/actions/serviceActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';
import Pagination from '../common/Pagination';

const ServiceDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { 
    selectedService: service, 
    loading, 
    error, 
    serviceAgencies,
    agenciesMeta 
  } = useSelector((state) => state.services);

  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [agenciesPage, setAgenciesPage] = useState(1);

  // Styles object
  const styles = {
    container: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    backButton: {
      padding: '0.5rem',
      backgroundColor: '#f3f4f6',
      border: 'none',
      borderRadius: '0.375rem',
      cursor: 'pointer',
      color: '#374151',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.2s',
    },
    backButtonHover: {
      backgroundColor: '#e5e7eb',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '0.75rem',
    },
    actionButton: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
    },
    editButton: {
      backgroundColor: '#4f46e5',
      color: '#ffffff',
    },
    editButtonHover: {
      backgroundColor: '#4338ca',
    },
    toggleButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
    },
    toggleButtonHover: {
      backgroundColor: '#047857',
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff',
    },
    deleteButtonHover: {
      backgroundColor: '#b91c1c',
    },
    icon: {
      width: '1.25rem',
      height: '1.25rem',
    },
    content: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      overflow: 'hidden',
    },
    hero: {
      padding: '2rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    heroContent: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    serviceIcon: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      backgroundColor: '#e5e7eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '2rem',
      color: '#4b5563',
    },
    serviceInfo: {
      flex: 1,
    },
    serviceName: {
      fontSize: '2rem',
      fontWeight: 700,
      color: '#111827',
      marginBottom: '0.5rem',
    },
    serviceDescription: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '1rem',
    },
    badge: {
      display: 'inline-flex',
      padding: '0.25rem 0.75rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    activeBadge: {
      backgroundColor: '#d1fae5',
      color: '#065f46',
    },
    inactiveBadge: {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1.5rem',
      marginTop: '1.5rem',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '1rem',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
    },
    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginBottom: '0.5rem',
    },
    statValue: {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: '#111827',
    },
    tabContent: {
      padding: '2rem',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '2rem',
    },
    infoSection: {
      marginBottom: '2rem',
    },
    infoTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1rem',
    },
    infoList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    infoItem: {
      display: 'flex',
      padding: '0.75rem 0',
      borderBottom: '1px solid #f3f4f6',
    },
    infoLabel: {
      width: '120px',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#6b7280',
    },
    infoValue: {
      flex: 1,
      fontSize: '0.875rem',
      color: '#111827',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    tableHeader: {
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    tableHeaderCell: {
      padding: '0.75rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: '#6b7280',
    },
    tableRow: {
      borderBottom: '1px solid #e5e7eb',
    },
    tableCell: {
      padding: '1rem 1.5rem',
      fontSize: '0.875rem',
      color: '#111827',
    },
    agencyLink: {
      color: '#2563eb',
      textDecoration: 'none',
      fontWeight: 500,
    },
    entityName: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    locationInfo: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    contactInfo: {
      fontSize: '0.875rem',
      color: '#6b7280',
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem 1.5rem',
      color: '#6b7280',
      fontSize: '0.875rem',
    },
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchService(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (activeTab === 'agencies' && id) {
      dispatch(fetchServiceAgencies(id, { page: agenciesPage, limit: 10 }));
    }
  }, [dispatch, id, activeTab, agenciesPage]);

  const handleDelete = useCallback(async () => {
    try {
      await dispatch(deleteService(id));
      navigate('/admin/services');
    } catch (error) {
      console.error('Delete failed:', error);
    }
  }, [dispatch, id, navigate]);

  const handleToggleStatus = useCallback(async () => {
    try {
      await dispatch(toggleServiceStatus(id));
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  }, [dispatch, id]);

  if (loading && !service) {
    return <LoadingSpinner fullScreen />;
  }

  if (!service && !loading) {
    return (
      <div style={styles.container}>
        <Alert
          type="error"
          message="Service non trouvé"
        />
        <Link to="/admin/services" style={styles.backButton}>
          Retour à la liste
        </Link>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Aperçu' },
    { id: 'agencies', label: `Agences (${service?._count?.agencies || 0})` },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button
            onClick={() => navigate('/admin/services')}
            style={styles.backButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 style={styles.title}>Détails du service</h1>
        </div>
        <div style={styles.headerActions}>
          <Link
            to={`/admin/services/${id}/edit`}
            style={{
              ...styles.actionButton,
              ...styles.editButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Modifier
          </Link>
          <button
            onClick={handleToggleStatus}
            style={{
              ...styles.actionButton,
              ...styles.toggleButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.toggleButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.toggleButton.backgroundColor;
            }}
          >
            {service?.isActive ? (
              <>
                <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Désactiver
              </>
            ) : (
              <>
                <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                Activer
              </>
            )}
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              ...styles.actionButton,
              ...styles.deleteButton,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
            }}
          >
            <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Supprimer
          </button>
        </div>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearServiceError())}
        />
      )}

      <div style={styles.content}>
        {/* Hero Section */}
        <div style={styles.hero}>
          <div style={styles.heroContent}>
            <div style={styles.serviceIcon}>
              {service.icon || '🔧'}
            </div>
            <div style={styles.serviceInfo}>
              <h2 style={styles.serviceName}>{service.name}</h2>
              {service.description && (
                <p style={styles.serviceDescription}>{service.description}</p>
              )}
              <div>
                <span
                  style={{
                    ...styles.badge,
                    ...(service.isActive ? styles.activeBadge : styles.inactiveBadge),
                  }}
                >
                  {service.isActive ? 'Actif' : 'Inactif'}
                </span>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Agences</div>
              <div style={styles.statValue}>{service._count?.agencies || 0}</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statLabel}>Date de création</div>
              <div style={styles.statValue}>
                {new Date(service.createdAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div style={styles.tabContent}>
          {activeTab === 'overview' && (
            <div style={styles.infoGrid}>
              {/* Service Information */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Informations</h3>
                <ul style={styles.infoList}>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Nom</span>
                    <span style={styles.infoValue}>{service.name}</span>
                  </li>
                  {service.description && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Description</span>
                      <span style={styles.infoValue}>{service.description}</span>
                    </li>
                  )}
                  {service.icon && (
                    <li style={styles.infoItem}>
                      <span style={styles.infoLabel}>Icône</span>
                      <span style={styles.infoValue}>{service.icon}</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Metadata */}
              <div style={styles.infoSection}>
                <h3 style={styles.infoTitle}>Métadonnées</h3>
                <ul style={styles.infoList}>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Créé le</span>
                    <span style={styles.infoValue}>
                      {new Date(service.createdAt).toLocaleString()}
                    </span>
                  </li>
                  <li style={styles.infoItem}>
                    <span style={styles.infoLabel}>Modifié le</span>
                    <span style={styles.infoValue}>
                      {new Date(service.updatedAt).toLocaleString()}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'agencies' && (
            <div>
              {serviceAgencies && serviceAgencies.length > 0 ? (
                <>
                  <table style={styles.table}>
                    <thead style={styles.tableHeader}>
                      <tr>
                        <th style={styles.tableHeaderCell}>Agence</th>
                        <th style={styles.tableHeaderCell}>Entité</th>
                        <th style={styles.tableHeaderCell}>Localisation</th>
                        <th style={styles.tableHeaderCell}>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serviceAgencies.map((agency) => (
                        <tr key={agency.id} style={styles.tableRow}>
                          <td style={styles.tableCell}>
                            <Link to={`/admin/agencies/${agency.id}/details`} style={styles.agencyLink}>
                              {agency.name}
                            </Link>
                          </td>
                          <td style={styles.tableCell}>
                            <span style={styles.entityName}>
                              {agency.entity?.name || 'N/A'}
                            </span>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={styles.locationInfo}>
                              {agency.address && <div>{agency.address}</div>}
                              {(agency.city || agency.zipCode) && (
                                <div>{[agency.city, agency.zipCode].filter(Boolean).join(' ')}</div>
                              )}
                            </div>
                          </td>
                          <td style={styles.tableCell}>
                            <div style={styles.contactInfo}>
                              {agency.phone && <div>📞 {agency.phone}</div>}
                              {agency.email && <div>✉️ {agency.email}</div>}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Pagination
                    currentPage={agenciesMeta.page}
                    totalPages={agenciesMeta.totalPages}
                    onPageChange={setAgenciesPage}
                  />
                </>
              ) : (
                <div style={styles.emptyState}>
                  Aucune agence n'utilise ce service pour le moment
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer le service"
        message={`Êtes-vous sûr de vouloir supprimer le service "${service?.name}" ? Cette action pourrait affecter les agences qui utilisent ce service.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
      />
    </div>
  );
};

export default ServiceDetail;